/**
 * Data Access Layer - Metrics Repository
 * Handles all metrics and monitoring data operations
 */

import api from '../interceptors/interceptor';
import { API_ROUTES } from '../constants/apiRoutes';

export interface RequestMetric {
  time: string;
  total: number;
  errors: number;
  errorRate?: number;
}

export interface RequestsOverTimeResponse {
  data: {
    time_range: string;
    granularity: string;
    model: string | null;
    start_time: string;
    end_time: string;
    data: Array<{
      timestamp: string;
      success_count: number;
      error_count: number;
    }>;
  };
}

export interface TokenUsageMetric {
  time: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface TokenUsageResponse {
  data: {
    time_range: string;
    granularity: string;
    model: string | null;
    start_time: string;
    end_time: string;
    total_input_tokens: number;
    total_output_tokens: number;
    total_tokens: number;
    data: Array<{
      timestamp: string;
      input_tokens: number;
      output_tokens: number;
      total_tokens: number;
    }>;
  };
}

export interface MetricsSummaryResponse {
  data: {
    range: string;
    model: string | null;
    summary: {
      total_requests: number;
      avg_latency_seconds: number;
      success_rate_percent: number;
      error_rate_percent: number;
      gpu_utilization_percent: number;
    };
  };
}

export interface MetricsSummary {
  totalRequests: number;
  totalErrors: number;
  errorRate: number;
  avgLatency: number;
  activeInstances: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  successRate?: number;
  gpuUtilization?: number;
}

export class MetricsRepository {
  private static instance: MetricsRepository;

  private constructor() {}

  /**
   * Singleton pattern - ensure only one instance exists
   */
  public static getInstance(): MetricsRepository {
    if (!MetricsRepository.instance) {
      MetricsRepository.instance = new MetricsRepository();
    }
    return MetricsRepository.instance;
  }

  /**
   * Get requests over time with error tracking
   */
  public async getRequestsOverTime(timeRange: string = '24h'): Promise<RequestMetric[]> {
    const response = await api.get(API_ROUTES.METRICS_REQUESTS_OVER_TIME(timeRange));
    const apiData: RequestsOverTimeResponse = response?.data;
    
    if (apiData && apiData.data && apiData.data.data) {
      // Transform API response to RequestMetric format
      return apiData.data.data.map(item => {
        const total = item.success_count + item.error_count;
        return {
          time: item.timestamp,
          total: total,
          errors: item.error_count,
          errorRate: total > 0 ? (item.error_count / total) * 100 : 0
        };
      });
    }
    
    return [];
  }

  /**
   * Get error rate metrics
   */
  public async getErrorRate(timeRange: string = '24h'): Promise<RequestMetric[]> {
    const response = await api.get(API_ROUTES.METRICS_ERROR_RATE(timeRange));
    return response?.data || [];
  }

  /**
   * Get token usage metrics over time
   */
  public async getTokenUsage(timeRange: string = '24h'): Promise<TokenUsageMetric[]> {
    const response = await api.get(API_ROUTES.METRICS_TOKENS(timeRange));
    const apiData: TokenUsageResponse = response?.data;
    
    if (apiData && apiData.data && apiData.data.data) {
      // Transform API response to TokenUsageMetric format
      return apiData.data.data.map(item => ({
        time: item.timestamp,
        inputTokens: item.input_tokens,
        outputTokens: item.output_tokens,
        totalTokens: item.total_tokens
      }));
    }
    
    return [];
  }

  /**
   * Get summary metrics
   */
  public async getSummaryMetrics(timeRange: string = '24h'): Promise<MetricsSummary> {
    const response = await api.get(API_ROUTES.METRICS_SUMMARY(timeRange));
    const apiData: MetricsSummaryResponse = response?.data;
    
    if (apiData && apiData.data && apiData.data.summary) {
      const summary = apiData.data.summary;
      // Calculate total errors from error rate and total requests
      const totalErrors = Math.round((summary.error_rate_percent / 100) * summary.total_requests);
      
      // Transform API response to MetricsSummary format
      return {
        totalRequests: summary.total_requests,
        totalErrors: totalErrors,
        errorRate: summary.error_rate_percent,
        avgLatency: summary.avg_latency_seconds * 1000, // Convert seconds to milliseconds
        activeInstances: 0, // Not provided by API
        successRate: summary.success_rate_percent,
        gpuUtilization: summary.gpu_utilization_percent,
        inputTokens: 0, // Not provided by this API
        outputTokens: 0, // Not provided by this API
        totalTokens: 0 // Not provided by this API
      };
    }
    
    // Return empty state if no data received
    return {
      totalRequests: 0,
      totalErrors: 0,
      errorRate: 0,
      avgLatency: 0,
      activeInstances: 0,
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0
    };
  }
}
